import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CouponDetailDialog } from '@/components/coupon-detail-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { Pagination } from '@/components/shared/pagination';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api, type OffsetPage } from '@/lib/api';
import {
  couponDiscountLabel,
  couponDurationLabel,
  couponUsesLabel,
  type PlatformCoupon,
} from '@/lib/coupon';
import { formatMoney } from '@/utils/format';

const BILLING_CURRENCY = 'GHS';

export const ChurchCouponsDialog = ({
  churchId,
  churchName,
  open,
  onOpenChange,
}: {
  churchId: string | null;
  churchName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [selectedCoupon, setSelectedCoupon] = useState<PlatformCoupon | null>(null);

  useEffect(() => {
    if (open) return;
    setPage(1);
    setSelectedCoupon(null);
  }, [open]);

  const coupons = useQuery({
    queryKey: ['platform-coupons', { churchId, page, pageSize }],
    queryFn: () =>
      api.get<{ data: PlatformCoupon[]; page: OffsetPage }>('/platform/billing/coupons', {
        query: { page, pageSize, churchId: churchId ?? undefined },
      }),
    enabled: open && Boolean(churchId),
  });

  const selectedFromPage =
    coupons.data?.data.find((row) => row.id === selectedCoupon?.id) ?? selectedCoupon;

  const columns: DataTableColumn<PlatformCoupon>[] = useMemo(
    () => [
      { id: 'code', header: t('billing.coupons.code'), cell: (row) => row.code },
      {
        id: 'discount',
        header: t('billing.coupons.discountType'),
        cell: (row) => couponDiscountLabel(row, t, (minor) => formatMoney(minor, BILLING_CURRENCY)),
      },
      {
        id: 'duration',
        header: t('billing.coupons.duration'),
        cell: (row) => couponDurationLabel(row, t),
        hideBelowMd: true,
      },
      {
        id: 'church',
        header: t('billing.coupons.assignedChurch'),
        cell: (row) => row.assignedChurchName ?? t('billing.coupons.assignedChurchAny'),
        hideBelowMd: true,
      },
      {
        id: 'uses',
        header: t('billing.coupons.redemptions'),
        cell: (row) => couponUsesLabel(row, t),
        hideBelowMd: true,
      },
      {
        id: 'status',
        header: t('billing.coupons.status'),
        cell: (row) => (
          <Badge variant={row.active ? 'success' : 'neutral'}>
            {row.active ? t('billing.coupons.active') : t('billing.coupons.inactive')}
          </Badge>
        ),
      },
    ],
    [t],
  );

  const pageMeta = coupons.data?.page;
  const close = () => onOpenChange(false);

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && close()}>
        <DialogContent closeLabel={tCommon('action.close')} size="xl">
          <DialogHeader>
            <DialogTitle>{t('churches.couponsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('churches.couponsDialog.description', { name: churchName })}
            </DialogDescription>
          </DialogHeader>

          <DialogBody contentClassName="flex flex-col gap-6">
            {coupons.isError ? (
              <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
            ) : (
              <>
                <DataTable
                  columns={columns}
                  rows={coupons.data?.data ?? []}
                  rowKey={(row) => row.id}
                  loading={coupons.isLoading}
                  empty={
                    <p className="py-8 text-center text-body-sm text-fg-secondary">
                      {t('churches.couponsDialog.empty')}
                    </p>
                  }
                  onRowClick={setSelectedCoupon}
                />
                {pageMeta && pageMeta.totalPages > 1 ? (
                  <Pagination
                    page={pageMeta.page}
                    pageSize={pageMeta.pageSize}
                    totalItems={pageMeta.totalItems}
                    totalPages={pageMeta.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next);
                      setPage(1);
                    }}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                ) : null}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={close}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CouponDetailDialog
        coupon={selectedFromPage}
        open={selectedCoupon !== null}
        onOpenChange={(next) => {
          if (!next) setSelectedCoupon(null);
        }}
        canManage
      />
    </>
  );
};
